import { useCallback, useEffect, useState } from 'react';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';

// T-UI-012 — Audit Log: paginated read of the append-only `auditLogs` (server-written;
// create-only in rules). Tenant-scoped; visible to owner/hqadmin/manager (route-gated).
const PAGE = 25;

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AuditLog() {
  const { tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (after) => {
    if (!tenantId) return;
    setLoading(true); setError('');
    try {
      const parts = [where('tenantId', '==', tenantId), orderBy('ts', 'desc')];
      if (after) parts.push(startAfter(after));
      parts.push(limit(PAGE));
      const snap = await getDocs(query(collection(db, 'auditLogs'), ...parts));
      const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows((prev) => (after ? [...prev, ...batch] : batch));
      setCursor(snap.docs[snap.docs.length - 1] ?? null);
      if (batch.length < PAGE) setDone(true);
    } catch (e) {
      setError(e.message || 'Could not load the audit log.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { setRows([]); setCursor(null); setDone(false); load(null); }, [load]);

  return (
    <div>
      {error && <div className="form-error">{error}</div>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>When</th><th>Who</th><th>Record</th><th>Field</th><th>Change</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="audit-row">
                <td>{fmt(r.ts)}</td>
                <td>{r.displayName ?? r.uid ?? 'system'}</td>
                <td className="muted">{r.collection}/{r.docId}</td>
                <td>{r.field}</td>
                <td><span className="diff-old">{String(r.oldValue ?? '—')}</span> → <span className="diff-new">{String(r.newValue ?? '—')}</span></td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>No audited changes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--space-4)' }}>
        {!done && rows.length > 0 && (
          <button className="btn btn-ghost btn-sm" disabled={loading} onClick={() => load(cursor)}>{loading ? 'Loading…' : 'Load more'}</button>
        )}
      </div>
      <p className="muted" style={{ fontSize: 'var(--text-caption)', marginTop: 'var(--space-3)' }}>
        Append-only and server-written — entries cannot be edited or deleted by anyone.
      </p>
    </div>
  );
}
